import { Component, inject, computed, effect, signal } from '@angular/core';
import { CommonModule, JsonPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { 
  AuthenticationStore, 
  AuthPhase,
  AuthRefreshTypesEnum 
} from '@argo/authentication';
import { ConfigStore } from '@argo/config';
import { 
  ScaModuleTitleComponent,
  ScaCardWorkshopComponent,
  ScaBadgeComponent,
  ScaAlertComponent,
  ScaIconComponent,
  ScaButtonComponent,
  ScaProgressBarComponent,
  ScaClaimCardComponent,
  ScaViewMoreComponent
} from '@argo-dev-components-sca-angular/dev-components-sca';

@Component({
  selector: 'app-auth-test-complete',
  standalone: true,
  imports: [CommonModule, JsonPipe, RouterLink, ScaModuleTitleComponent, ScaCardWorkshopComponent, ScaBadgeComponent, ScaAlertComponent, ScaIconComponent, ScaButtonComponent, ScaProgressBarComponent, ScaClaimCardComponent, ScaViewMoreComponent],
  templateUrl: './auth-test.component.html',
  styleUrl: './auth-test.component.scss'
})
export class AuthTestCompleteComponent {
  readonly auth = inject(AuthenticationStore);
  readonly config = inject(ConfigStore);
  
  readonly AuthPhase = AuthPhase;
  readonly AuthRefreshTypesEnum = AuthRefreshTypesEnum;
  
  readonly decodedToken = signal<any>(null);
  
  // View More properties
  readonly collapsedText = 'Ver JSON Completo del Usuario';
  readonly expandedText = 'Ocultar JSON del Usuario';
  readonly isExpanded = false;
  
  // Computed properties
  readonly accessToken = computed(() => this.auth.getToken() || 'No disponible');
  readonly idToken = computed(() => this.auth.getIdToken() || 'No disponible');
  readonly userRoles = computed(() => this.auth.getUserRoles());
  
  readonly tokenPercentage = computed(() => {
    const remaining = this.auth.tokenRemainingSeconds();
    const total = this.currentTokenLifeSeconds();
    return total > 0 ? Math.round((remaining / total) * 100) : 0;
  });
  
  readonly currentTokenLifeSeconds = computed(() => {
    return Math.round(this.auth.currentTokenLifeMs / 1000);
  });
  
  readonly elapsedSeconds = computed(() => {
    return this.currentTokenLifeSeconds() - this.auth.tokenRemainingSeconds();
  });
  
  readonly refreshType = computed(() => {
    const appConfig = this.config.getConfig();
    return (appConfig.arq.authentication?.refreshTypes?.[0] as AuthRefreshTypesEnum) ?? AuthRefreshTypesEnum.NOREFRESHABLE;
  });
  
  readonly coverage = 100; // 100% de funcionalidades implementadas
  
  copyToClipboard(text: string, label: string = 'Token'): void {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        alert(`✅ ${label} copiado al portapapeles!`);
      }).catch(() => {
        alert('❌ Error al copiar al portapapeles');
      });
    } else {
      alert('❌ Clipboard API no disponible');
    }
  }
  
  decodeToken(token: string): void {
    try {
      if (!token || token === 'No disponible') {
        alert('⚠️ No hay token disponible');
        return;
      }
      
      const parts = token.split('.');
      if (parts.length !== 3) {
        alert('❌ Token JWT inválido');
        return;
      }
      
      const payload = JSON.parse(atob(parts[1]));
      this.decodedToken.set(payload);
    } catch (e) {
      alert('❌ Error al decodificar el token');
      console.error(e);
    }
  }
}
